// Ophalen van de Funding-tab.
//
// EIGEN FETCH-WRAPPER, BEWUST EN TIJDELIJK — zelfde afweging en zelfde
// precedent als lib/redactie.ts. `lib/api.ts` is de enige plek waar dit
// dashboard het netwerk aanraakt en dat hoort zo te blijven, maar dat bestand
// wordt vandaag door parallelle sessies bewerkt en twee schrijvers in één
// bestand is precies waar W15 en W16 op stukliepen. Dus lenen we `ApiError`
// (dat is de foutafhandeling die het scherm toont) en doen we de aanroep zelf.
// Samenvoegen zodra die sessies klaar zijn; het is één functie verplaatsen.
//
// `api.funding()` in api.ts staat er nog en is sinds deze sessie ongebruikt —
// hij haalt de lijst zonder filters op en kent de nieuwe vorm niet. Ook die
// gaat mee bij het samenvoegen.

import type {
  FundingDetailResponse,
  FundingListResponse,
  FundingVoorResponse,
} from "../contracts";
import { ApiError, wieKoppen } from "./api";
import { useResource, type Resource } from "./useResource";

async function haal<T>(pad: string): Promise<T> {
  let res: globalThis.Response;
  try {
    res = await fetch(`/api${pad}`, { headers: { Accept: "application/json", ...(await wieKoppen()) } });
  } catch (err) {
    throw new ApiError(0, pad, err instanceof Error ? err.message : "netwerkfout");
  }
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { detail?: unknown };
      if (typeof body.detail === "string") detail = body.detail;
    } catch {
      // geen json-body; de statusregel is dan het beste wat we hebben
    }
    throw new ApiError(res.status, pad, detail);
  }
  return (await res.json()) as T;
}

export type FundingFilters = {
  sector?: readonly string[];
  thema?: readonly string[];
  verstrekkerType?: readonly string[];
  status?: readonly string[];
  venster?: readonly string[];
  aanvraagmoment?: readonly string[];
  /** Aantal dagen; toont alleen regelingen die binnen die termijn sluiten. */
  sluitBinnen?: number | null;
  zoek?: string;
  sorteer?: "venster" | "bedrag" | "naam";
  page?: number;
  pageSize?: number;
  /** Betrouwbaar register (08-10-2026): bevestigd · waarschijnlijk · niet_zeker. */
  trap?: readonly string[];
};

function query(f: FundingFilters): string {
  const p = new URLSearchParams();
  const lijsten: [string, readonly string[] | undefined][] = [
    ["sector", f.sector],
    ["thema", f.thema],
    ["verstrekkerType", f.verstrekkerType],
    ["status", f.status],
    ["venster", f.venster],
    ["aanvraagmoment", f.aanvraagmoment],
    ["trap", f.trap],
  ];
  for (const [naam, waarden] of lijsten) {
    for (const w of waarden ?? []) p.append(naam, w);
  }
  if (f.sluitBinnen) p.set("sluitBinnen", String(f.sluitBinnen));
  if (f.zoek?.trim()) p.set("zoek", f.zoek.trim());
  if (f.sorteer) p.set("sorteer", f.sorteer);
  if (f.page && f.page > 1) p.set("page", String(f.page));
  if (f.pageSize) p.set("pageSize", String(f.pageSize));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function useFunding(f: FundingFilters, enabled = true): Resource<FundingListResponse> {
  const q = query(f);
  return useResource<FundingListResponse>(() => haal(`/funding${q}`), [q], enabled);
}

export function haalRegeling(id: string): Promise<FundingDetailResponse> {
  return haal<FundingDetailResponse>(`/funding/${id}`);
}

/** Eén lijst ophalen buiten een hook (W5: de cache per bezoek van de Plan-tab). */
export function haalFundingLijst(f: FundingFilters): Promise<FundingListResponse> {
  return haal<FundingListResponse>(`/funding${query(f)}`);
}

// ── de regelingenstrook (funding fase 5, F5 a + G4 a) ─────────────────────────
//
// GET /api/funding/voor: de open en aangekondigde regelingen voor een sector,
// gerangschikt op de pasmaat. Skill-detail, trend-detail en de Plan-tab lezen
// alle drie deze ene route via RegelingenStrook.tsx.

export type FundingVoorFilters = {
  /** Sleutel uit config/sectors.yaml, of "sectorbreed". */
  sector: string;
  /** Vorm van de kans (sleutel uit config/funding.yaml#pasmaat.vormen). */
  vorm?: string | null;
  bedrijfstype?: readonly string[];
  limiet?: number;
};

export function voorQuery(f: FundingVoorFilters): string {
  const p = new URLSearchParams();
  p.set("sector", f.sector);
  if (f.vorm) p.set("vorm", f.vorm);
  for (const b of f.bedrijfstype ?? []) p.append("bedrijfstype", b);
  if (f.limiet) p.set("limiet", String(f.limiet));
  return `?${p.toString()}`;
}

export function useFundingVoor(f: FundingVoorFilters): Resource<FundingVoorResponse> {
  const q = voorQuery(f);
  return useResource<FundingVoorResponse>(() => haal(`/funding/voor${q}`), [q]);
}
